import { useMemo } from 'react';
import { useGetUnlayerUserSignaturesQuery } from '@/features/unlayerUserSignatures/unlayerUserSignaturesApi';
import {
	buildLegacySignatureOptions,
	buildUnlayerSignatureOptions,
	getAvailableSignatureEmailOptions,
} from '@/features/unlayerUserSignatures/helpers';
import { NONE_SIGNATURE_OPTION } from '@/components/Drawers/AddUnlayerEmailDrawer/constants';
import { normalizeLegacySignatures } from '@/components/Drawers/AddUnlayerEmailDrawer/hooks/signatureSelection/normalizeLegacySignatures';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

/**
 * Loads signature sources for the compose dialog and normalizes them into one
 * selector model so the rest of the hook can focus on selection behavior.
 */
export const useSignatureSourceOptions = () => {
	const open = useSelector((state) => state.dialog?.open?.unlayerEmail) ?? false;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const signatureOwnerId = userDetails?.userId ?? null;
	const availableEmailOptions = getAvailableSignatureEmailOptions();

	const availableEmails = useMemo(
		() => availableEmailOptions.map((option) => option.email),
		[availableEmailOptions],
	);
	const { data: unlayerSignatureRecords = [], isFetching: isFetchingUnlayerSignatures } =
		useGetUnlayerUserSignaturesQuery(
			{
				userId: signatureOwnerId,
				availableEmails,
			},
			{
				skip: !open || !signatureOwnerId || availableEmails.length === 0,
			},
		);
	const legacySignatures = useMemo(() => normalizeLegacySignatures(userDetails), [userDetails]);
	const unlayerOptions = useMemo(
		() =>
			buildUnlayerSignatureOptions({
				emailOptions: availableEmailOptions,
				signatureRecords: unlayerSignatureRecords,
			}),
		[availableEmailOptions, unlayerSignatureRecords],
	);
	const legacyOptions = useMemo(
		() =>
			buildLegacySignatureOptions({
				emailOptions: availableEmailOptions,
				legacySignatures,
			}),
		[availableEmailOptions, legacySignatures],
	);
	const activeSignatureOptions = useMemo(
		() => (unlayerOptions.length > 0 ? unlayerOptions : legacyOptions),
		[legacyOptions, unlayerOptions],
	);
	const activeSource = unlayerOptions.length > 0 ? 'unlayer' : 'legacy';
	const signatureOptions = useMemo(
		() => [NONE_SIGNATURE_OPTION, ...activeSignatureOptions],
		[activeSignatureOptions],
	);
	const isSignatureDisabled = activeSignatureOptions.length === 0;
	const signatureDisabledReason = useMemo(() => {
		if (!isSignatureDisabled) {
			return '';
		}
		if (unlayerOptions.length === 0 && legacyOptions.length === 0) {
			return 'Set up a signature in your profile to use signatures here.';
		}
		return 'No signatures are available for your current email addresses.';
	}, [isSignatureDisabled, legacyOptions.length, unlayerOptions.length]);

	return {
		activeSignatureOptions,
		activeSource,
		isFetchingUnlayerSignatures,
		isSignatureDisabled,
		signatureDisabledReason,
		signatureOptions,
	};
};
